import { supabase } from '@/config/supabase'
import {
  ZONE_QUOTAZIONI,
  COEFFICIENTI_EXTRA,
  type ZoneQuotazione,
} from '@/data/zone-quotazioni'

// Cache in memoria con TTL per evitare fetch ripetuti ma garantire dati freschi
const CACHE_TTL_MS = 5 * 60 * 1000 // 5 minuti

let cachedZones: ZoneQuotazione[] | null = null
let cachedZonesTimestamp = 0
let cachedCoefficienti: typeof COEFFICIENTI_EXTRA | null = null
let cachedCoefficientiTimestamp = 0

function isCacheValid(timestamp: number): boolean {
  return Date.now() - timestamp < CACHE_TTL_MS
}

export async function fetchZoneQuotazioni(): Promise<ZoneQuotazione[]> {
  if (cachedZones && isCacheValid(cachedZonesTimestamp)) return cachedZones

  try {
    const { data, error } = await supabase
      .from('zone_quotazioni_sanremo')
      .select('*')
      .order('nome')

    if (error || !data || data.length === 0) {
      cachedZones = ZONE_QUOTAZIONI
      return cachedZones
    }

    cachedZones = data.map((row) => ({
      nome: row.nome,
      prezzoAbitabile: Number(row.prezzo_abitabile),
      prezzoRistrutturato: Number(row.prezzo_ristrutturato),
      prezzoDaRistrutturare: Number(row.prezzo_da_ristrutturare),
      vistaMareBonus: Number(row.vista_mare_bonus),
      pertinenzaBonus: Number(row.pertinenza_bonus),
      esposizioneBonus: Number(row.esposizione_bonus),
      spaziEsterniBonus: Number(row.spazi_esterni_bonus),
      senzaAscensore: Number(row.senza_ascensore),
      casa: Number(row.casa),
      villa: Number(row.villa),
    }))
    cachedZonesTimestamp = Date.now()
    return cachedZones
  } catch {
    cachedZones = ZONE_QUOTAZIONI
    return cachedZones
  }
}

export async function fetchCoefficientiExtra(): Promise<typeof COEFFICIENTI_EXTRA> {
  if (cachedCoefficienti && isCacheValid(cachedCoefficientiTimestamp)) return cachedCoefficienti

  try {
    const { data, error } = await supabase
      .from('coefficienti_extra_sanremo')
      .select('*')

    if (error || !data || data.length === 0) {
      cachedCoefficienti = { ...COEFFICIENTI_EXTRA }
      return cachedCoefficienti
    }

    const result = { ...COEFFICIENTI_EXTRA }
    data.forEach((row) => {
      if (row.key in result) {
        ;(result as Record<string, number>)[row.key] = Number(row.value)
      }
    })
    cachedCoefficienti = result
    cachedCoefficientiTimestamp = Date.now()
    return cachedCoefficienti
  } catch {
    cachedCoefficienti = { ...COEFFICIENTI_EXTRA }
    cachedCoefficientiTimestamp = Date.now()
    return cachedCoefficienti
  }
}

// Salva tutte le zone su Supabase (upsert per nome)
export async function saveZoneQuotazioni(zones: ZoneQuotazione[]): Promise<void> {
  const rows = zones.map((z) => ({
    nome: z.nome,
    prezzo_abitabile: z.prezzoAbitabile,
    prezzo_ristrutturato: z.prezzoRistrutturato,
    prezzo_da_ristrutturare: z.prezzoDaRistrutturare,
    vista_mare_bonus: z.vistaMareBonus,
    pertinenza_bonus: z.pertinenzaBonus,
    esposizione_bonus: z.esposizioneBonus,
    spazi_esterni_bonus: z.spaziEsterniBonus,
    senza_ascensore: z.senzaAscensore,
    casa: z.casa,
    villa: z.villa,
    updated_at: new Date().toISOString(),
  }))

  const { error } = await supabase
    .from('zone_quotazioni_sanremo')
    .upsert(rows, { onConflict: 'nome' })

  if (error) throw error
  cachedZones = null
}

// Salva coefficienti extra su Supabase (upsert per key)
export async function saveCoefficientiExtra(
  coefficienti: typeof COEFFICIENTI_EXTRA
): Promise<void> {
  const rows = Object.entries(coefficienti).map(([key, value]) => ({
    key,
    value,
    updated_at: new Date().toISOString(),
  }))

  const { error } = await supabase
    .from('coefficienti_extra_sanremo')
    .upsert(rows, { onConflict: 'key' })

  if (error) throw error
  cachedCoefficienti = null
}

// Elimina zona da Supabase
export async function deleteZone(zoneName: string): Promise<void> {
  const { error } = await supabase
    .from('zone_quotazioni_sanremo')
    .delete()
    .eq('nome', zoneName)

  if (error) throw error
  cachedZones = null
}

// Recupera timestamp ultima modifica delle zone
export async function fetchLastUpdatedAt(): Promise<string | null> {
  try {
    const { data, error } = await supabase
      .from('zone_quotazioni_sanremo')
      .select('updated_at')
      .order('updated_at', { ascending: false })
      .limit(1)
      .single()

    if (error || !data) return null
    return data.updated_at
  } catch {
    return null
  }
}

// Invalida cache (utile dopo salvataggio admin)
export function invalidateCache() {
  cachedZones = null
  cachedZonesTimestamp = 0
  cachedCoefficienti = null
  cachedCoefficientiTimestamp = 0
}
